import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeft, Key, Shield, Plus, Copy, Check, BookOpen, X } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import type { Agent } from '@/types/shared'

interface ApiToken {
  id: number
  name: string
  token_hint: string
  last_used_at: string | null
  revoked_at: string | null
  created_at: string
}

interface ApiProps {
  currentAgent: Agent
  api_tokens: ApiToken[]
  new_token: string | null
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Nunca'
  const date = new Date(dateStr)
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function Api({ currentAgent, api_tokens, new_token }: ApiProps) {
  const [showForm, setShowForm] = useState(false)
  const [tokenName, setTokenName] = useState('')
  const [creating, setCreating] = useState(false)
  const [copied, setCopied] = useState(false)

  const copyToken = () => {
    if (new_token) {
      navigator.clipboard.writeText(new_token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCreate = () => {
    if (!tokenName.trim()) return
    setCreating(true)
    router.post('/settings/api', { name: tokenName.trim() }, {
      onFinish: () => {
        setCreating(false)
        setShowForm(false)
        setTokenName('')
      },
    })
  }

  const handleRevoke = (token: ApiToken) => {
    if (confirm(`Revogar o token "${token.name}"? Essa ação não pode ser desfeita.`)) {
      router.delete(`/settings/api/${token.id}`)
    }
  }

  const activeTokens = api_tokens.filter((t) => !t.revoked_at)
  const revokedTokens = api_tokens.filter((t) => t.revoked_at)

  return (
    <>
      <Head title="API" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="api" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl">

                {/* New Token Banner */}
                {new_token && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                    <div className="flex items-start gap-3">
                      <Key className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">Token criado com sucesso</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Copie agora. Este token não será exibido novamente.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <code className="flex-1 text-sm bg-white border border-amber-300 rounded-lg px-4 py-3 font-mono text-amber-900 break-all select-all">
                            {new_token}
                          </code>
                          <button
                            type="button"
                            onClick={copyToken}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors flex-shrink-0"
                          >
                            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                            {copied ? 'Copiado!' : 'Copiar'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tokens Section */}
                <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-5 h-5 text-gray-700" />
                    <h2 className="text-lg font-semibold text-gray-900">Tokens da API</h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Tokens permitem que sistemas externos enviem mensagens via API.
                    Cada token tem acesso completo à organização.
                  </p>

                  {/* Create Token */}
                  {!showForm ? (
                    <button
                      type="button"
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Gerar Novo Token
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={tokenName}
                        onChange={(e) => setTokenName(e.target.value)}
                        placeholder="ex: CRM, Automação, N8N"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreate()
                          if (e.key === 'Escape') { setShowForm(false); setTokenName('') }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); setTokenName('') }}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleCreate}
                        disabled={creating || !tokenName.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {creating ? 'Criando...' : 'Criar Token'}
                      </button>
                    </div>
                  )}

                  {/* Token List */}
                  {api_tokens.length > 0 ? (
                    <div className="divide-y divide-gray-100 mt-6 border-t border-gray-100">
                      {activeTokens.map((token) => (
                        <div key={token.id} className="flex items-center justify-between py-4 group">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{token.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              <span className="font-mono">sk_...{token.token_hint}</span>
                              {' · '}
                              Criado em {formatDate(token.created_at)}
                              {' · '}
                              Último uso: {token.last_used_at ? formatDate(token.last_used_at) : 'Nunca'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                              Ativo
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRevoke(token)}
                              className="text-sm text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            >
                              Revogar
                            </button>
                          </div>
                        </div>
                      ))}
                      {revokedTokens.map((token) => (
                        <div key={token.id} className="flex items-center justify-between py-4 opacity-60">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{token.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              <span className="font-mono">sk_...{token.token_hint}</span>
                              {' · '}
                              Criado em {formatDate(token.created_at)}
                              {' · '}
                              Revogado em {formatDate(token.revoked_at)}
                            </p>
                          </div>
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 flex-shrink-0">
                            <X className="w-3 h-3" />
                            Revogado
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-8 text-center py-6">
                      <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-500">Nenhum token criado</p>
                      <p className="text-xs text-gray-400 mt-1">Crie um token para começar a usar a API</p>
                    </div>
                  )}
                </section>

                {/* API Documentation Section */}
                <section className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-5 h-5 text-gray-700" />
                    <h2 className="text-lg font-semibold text-gray-900">Uso da API</h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Use o endpoint abaixo para enviar mensagens de WhatsApp via template.
                    Inclua o token no header <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">Authorization</code>.
                  </p>

                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Enviar mensagem WhatsApp</p>
                    <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-3 overflow-x-auto">
{`curl -X POST https://seudominio.com/api/v1/messages/whatsapp \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "contact": {
      "phone": "+5531999887766",
      "name": "João Silva",
      "email": "joao@example.com",
      "user_id": "usr_123"
    },
    "template": {
      "name": "welcome_message",
      "language": "pt_BR",
      "variables": ["João", "Premium"]
    }
  }'`}
                    </pre>
                  </div>

                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 mb-1">Resposta de sucesso (201)</p>
                    <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-3 overflow-x-auto">
{`{
  "message_id": 123,
  "conversation_id": 456,
  "contact_id": 789,
  "status": "pending"
}`}
                    </pre>
                  </div>

                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Identificação do contato</p>
                    <p className="text-sm text-gray-600">
                      O contato é buscado na seguinte ordem: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">user_id</code> →{' '}
                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">email</code> →{' '}
                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">phone</code>.
                      Se não for encontrado, um novo contato é criado automaticamente.
                      O campo <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">phone</code> é obrigatório.
                    </p>
                  </div>
                </section>

              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
